import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Divider,
  Group,
  Modal,
  NumberInput,
  ScrollArea,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { IconEye, IconPlayerPlay, IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import "./migrationsapi.enhance";
import { useGetAppadminMigrationsQuery, usePostAppadminMigrationsRunMutation } from "../../../../openapi";

type MigrationStatus = "running" | "success" | "failed";

const statusColor: Record<MigrationStatus, string> = {
  running: "blue",
  success: "green",
  failed: "red",
};

const formatDate = (value?: string) => {
  if (!value) return "";
  return new Date(value).toLocaleString("sv-SE");
};

export const MigrationsAdmin = () => {
  const { data, error, isFetching, refetch } = useGetAppadminMigrationsQuery();
  const [runMigration, { data: runResult, error: runError, isLoading }] = usePostAppadminMigrationsRunMutation();
  const [userIdsText, setUserIdsText] = useState("");
  const [confirmRetirement, setConfirmRetirement] = useState(false);
  const userIds = userIdsText.split(/[\s,;]+/).filter(Boolean);
  const validUserIds =
    userIds.length > 0 &&
    userIds.length <= 100 &&
    userIds.every((id) => /^[a-f0-9]{24}$/i.test(id)) &&
    new Set(userIds).size === userIds.length;
  const [allowRerun, setAllowRerun] = useState(false);
  const [limit, setLimit] = useState<number | "">("");
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [detailName, setDetailName] = useState<string | null>(null);

  const errorMessage =
    (error as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
    (runError as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
    (runError as { message?: string } | undefined)?.message;

  const run = async (name: string) => {
    setSelectedName(name);
    await runMigration({
      body: {
        name,
        userIds: data?.find((migration) => migration.name === name)?.requiresUserIds ? userIds : undefined,
        allowRerun,
        limit: typeof limit === "number" ? limit : undefined,
      },
    });
  };

  const selectedLatestRun = selectedName ? data?.find((migration) => migration.name === selectedName)?.latestRun : null;
  const displayedRun = runResult?.name === selectedName ? runResult : selectedLatestRun;
  const detailMigration = detailName ? data?.find((migration) => migration.name === detailName) : null;
  const detailRun = detailMigration?.latestRun ?? null;

  return (
    <Card withBorder shadow="sm" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={3}>Migreringer</Title>
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={16} />}
            loading={isFetching}
            onClick={() => refetch()}
          >
            Oppdater
          </Button>
        </Group>

        <Group align="flex-end">
          <Checkbox
            label="Tillat ny kjøring av migreringer som allerede er fullført"
            checked={allowRerun}
            onChange={(event) => setAllowRerun(event.currentTarget.checked)}
          />
          <NumberInput
            label="Limit"
            placeholder="Valgfritt"
            min={1}
            allowDecimal={false}
            value={limit}
            onChange={(value) => setLimit(typeof value === "number" ? value : "")}
            w="10rem"
          />
        </Group>

        {errorMessage != null && (
          <Alert color="red" variant="light">
            {errorMessage}
          </Alert>
        )}

        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Migrering</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Sist kjørt</Table.Th>
              <Table.Th>Kjørt av</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(data ?? []).map((migration) => {
              const latestRun = migration.latestRun;
              const isCurrent = selectedName === migration.name && isLoading;
              const alreadySucceeded = latestRun?.status === "success";

              return (
                <Table.Tr key={migration.name}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text fw={600}>{migration.title}</Text>
                      <Text size="sm" c="dimmed">
                        {migration.description}
                      </Text>
                      <Code>{migration.name}</Code>
                      {migration.requiresUserIds && (
                        <Textarea
                          label="Gjennomgåtte bruker-ID-er"
                          description="Kjør kontrollen først. Lim inn ID-ene til de gamle kontoene som skal pensjoneres, skilt med komma eller linjeskift. Maks 100."
                          value={userIdsText}
                          onChange={(event) => setUserIdsText(event.currentTarget.value)}
                          error={userIdsText && !validUserIds ? "Oppgi unike, gyldige bruker-ID-er." : undefined}
                        />
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    {latestRun ? (
                      <Badge color={statusColor[latestRun.status]} variant="light">
                        {latestRun.status}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        Ikke kjørt
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>{formatDate(latestRun?.startedAt)}</Table.Td>
                  <Table.Td>{latestRun?.startedByName ?? ""}</Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Button
                        variant="light"
                        leftSection={<IconEye size={16} />}
                        onClick={() => setDetailName(migration.name)}
                      >
                        Detaljer
                      </Button>
                      <Button
                        leftSection={<IconPlayerPlay size={16} />}
                        loading={isCurrent}
                        disabled={
                          isLoading ||
                          (migration.requiresUserIds && !validUserIds) ||
                          (alreadySucceeded && !allowRerun) ||
                          latestRun?.status === "running"
                        }
                        onClick={() => (migration.requiresUserIds ? setConfirmRetirement(true) : run(migration.name))}
                      >
                        Kjør
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        <Modal opened={confirmRetirement} onClose={() => setConfirmRetirement(false)} title="Pensjoner valgte kontoer">
          <Stack>
            <Text>
              Du pensjonerer {userIds.length} gamle kontoer og fjerner tilknyttede økter. Bekreft at identitetene er
              gjennomgått i kontrollens kjørelogg.
            </Text>
            <Code block>{userIds.join("\n")}</Code>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setConfirmRetirement(false)}>
                Avbryt
              </Button>
              <Button
                color="red"
                disabled={!validUserIds}
                onClick={() => {
                  setConfirmRetirement(false);
                  void run("retireReusedUpns");
                }}
              >
                Pensjoner kontoer
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={detailMigration != null}
          onClose={() => setDetailName(null)}
          title={detailMigration?.title ?? "Migrering"}
          size="xl"
        >
          {detailMigration != null && (
            <Stack gap="sm">
              <Stack gap={2}>
                <Text size="sm" c="dimmed">
                  {detailMigration.description}
                </Text>
                <Code>{detailMigration.name}</Code>
              </Stack>

              <Divider />

              {detailRun != null ? (
                <Stack gap="sm">
                  <Group gap="xs">
                    <Badge color={statusColor[detailRun.status]} variant="light">
                      {detailRun.status}
                    </Badge>
                    <Text size="sm">Startet {formatDate(detailRun.startedAt)}</Text>
                    {detailRun.finishedAt && <Text size="sm">Ferdig {formatDate(detailRun.finishedAt)}</Text>}
                  </Group>

                  <Table withTableBorder>
                    <Table.Tbody>
                      <Table.Tr>
                        <Table.Th>Run ID</Table.Th>
                        <Table.Td>
                          <Code>{detailRun.id}</Code>
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Th>Kjørt av</Table.Th>
                        <Table.Td>{detailRun.startedByName ?? detailRun.startedBy}</Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        <Table.Th>Bruker-ID</Table.Th>
                        <Table.Td>
                          <Code>{detailRun.startedBy}</Code>
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>

                  {detailRun.error && (
                    <Alert color="red" variant="light">
                      <Code block>{detailRun.error}</Code>
                    </Alert>
                  )}

                  <Stack gap="xs">
                    <Title order={4}>Logger</Title>
                    {detailRun.logs.length > 0 ? (
                      <ScrollArea h={420} type="auto">
                        <Stack gap={4}>
                          {detailRun.logs.map((line, index) => (
                            <Code key={`${detailRun.id}-modal-${index}`} block>
                              {line}
                            </Code>
                          ))}
                        </Stack>
                      </ScrollArea>
                    ) : (
                      <Text size="sm" c="dimmed">
                        Ingen logger lagret.
                      </Text>
                    )}
                  </Stack>

                  <Stack gap="xs">
                    <Title order={4}>Rådata</Title>
                    <ScrollArea h={220} type="auto">
                      <Code block>{JSON.stringify(detailRun, null, 2)}</Code>
                    </ScrollArea>
                  </Stack>
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">
                  Migreringen er ikke kjørt ennå.
                </Text>
              )}
            </Stack>
          )}
        </Modal>

        {displayedRun != null && (
          <Stack gap="xs">
            <Group gap="xs">
              <Badge color={statusColor[displayedRun.status]} variant="light">
                {displayedRun.status}
              </Badge>
              <Text size="sm">
                {displayedRun.name} startet {formatDate(displayedRun.startedAt)}
              </Text>
            </Group>

            {displayedRun.error && (
              <Alert color="red" variant="light">
                {displayedRun.error}
              </Alert>
            )}

            <ScrollArea h={220} type="auto">
              <Stack gap={4}>
                {displayedRun.logs.map((line, index) => (
                  <Code key={`${displayedRun.id}-${index}`} block>
                    {line}
                  </Code>
                ))}
              </Stack>
            </ScrollArea>
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
